import * as React from "react";
import { StyleSheet } from "react-native";
import Colors from "../../constants/Colors";
import { View, Text, Dropdown, Input, Button } from "../../components/Themed";
import PageHeader from "../../components/PageHeader";
import { FormProvider, useFieldArray, useForm } from "react-hook-form";
import { useEffect } from "react";
import { match } from "ts-pattern";
import {
  addForm,
  deleteFormById,
  getFormById,
  getForms,
  updateFormById,
} from "../../services/mock";
import BottomModal from "../../modals/BottomModal";
import { KeyboardAwareScrollView } from "react-native-keyboard-aware-scroll-view";
import Line from "../../components/Line";
import Icon from "../../components/Icon";
import { ControlType, Question, State } from "./types";
import FormQuestion from "./components/FormQuestion";
import { Orientation } from "../../types";

export const FormBuilder = ({ navigation }: any) => {
  const {
    control,
    register,
    handleSubmit,
    formState: { errors },
    setValue,
    getValues,
    watch,
    resetField,
  } = useForm();

  const { fields, append, remove, move } = useFieldArray({
    control,
    name: "questions",
  });

  const emptyQuestion: Question = {
    questionText: "",
    required: false,
    hidden: false,
    type: ControlType.Text,
    choices: [],
    formLabel: "",
  };

  type FormDropDown = {
    id: number;
    formName: string;
  };

  const [showBottomModal, setShowBottomModal] = React.useState(false);
  const [bottomModalText, setBottomModalText] = React.useState("");
  const [currentState, setCurrentState] = React.useState<State>(State.Default);
  const [forms, setForms] = React.useState<any[]>([]);

  const watchEditFormId = watch("editForm");
  const watchDeleteFormId = watch("deleteForm");

  const formMethods = useForm();

  useEffect(() => {
    const subscription = watch((value, { name, type }) => {
      if (type === "change" && name === "editForm") {
        if (value.editForm) {
          getFormById(value.editForm.value).then((form) => {
            setValue("id", form.id);
            setValue("formName", form.formName);
            setValue("questions", form.questions);

            setCurrentState(State.ReadyForEdit);
          });
        }
      } else if (type === "change" && name === "deleteForm") {
        if (value.deleteForm) {
          getFormById(value.deleteForm.value).then((form) => {
            setValue("formName", form.formName);
            setValue("questions", form.questions);
            setCurrentState(State.ReadyForDelete);
          });
        }
      }
    });
    return () => subscription.unsubscribe();
  }, [watch]);

  useEffect(() => {
    if (currentState === State.EditSelection)
      getForms().then((forms) => {
        const allForms = forms.map((form: FormDropDown) => {
          return {
            value: form.id,
            text: form.formName,
          };
        });
        setForms(allForms);
      });
  }, [currentState]);

  const navigateBack = () => {
    resetField("formName");
    resetField("questions");
    resetField("editForm");
    resetField("deleteForm");

    match(currentState)
      .with(State.Creating, () => setCurrentState(State.Default))
      .with(State.ReadyForEdit, () => setCurrentState(State.Default))
      .with(State.ReadyForDelete, () => setCurrentState(State.Default))
      .with(State.DeleteSelection, () => setCurrentState(State.Default))
      .with(State.EditSelection, () => setCurrentState(State.Default))
      .otherwise(() => {
        navigation.goBack();
      });
  };

  const deleteForm = () => {
    deleteFormById(watchDeleteFormId.value).then(() => {
      resetField("formName");
      resetField("questions");
      resetField("editForm");
      resetField("deleteForm");

      setForms([]);

      setCurrentState(State.Default);
      setBottomModalText("Form Deleted");
      setShowBottomModal(true);
    });
  };

  const save = (data: any) => {
    addForm(data).then((form) => {
      setBottomModalText("Form Saved");
      setShowBottomModal(true);

      setValue("editForm", { value: form.id, text: form.formName });

      setCurrentState(State.ReadyForEdit);
    });
  };

  const edit = (data: any) => {
    updateFormById(watchEditFormId.value, data).then((form) => {
      setBottomModalText("Form Updated");
      setShowBottomModal(true);
    });
  };

  const FormBuilder = (props: any) => (
    <View
      lightColor={Colors.base_colors_white}
      darkColor={Colors.nuetral_grey_600}
      style={styles.container}
    >
      {props.children}
    </View>
  );

  const FormBuilderTitle = () => (
    <Text
      size={18}
      lineHeight={24}
      weight="SemiBold"
      lightColor={Colors.nuetral_grey_700}
    >
      Forms
    </Text>
  );

  const BackButton = () => (
    <Icon
      name="arrow-left"
      size={24}
      color={Colors.primary_500}
      onPress={() => navigateBack()}
    />
  );

  const EditButton = () => (
    <Icon
      name="save"
      size={24}
      color={Colors.primary_500}
      onPress={handleSubmit(edit)}
    />
  );

  const SaveButton = () => (
    <Icon
      name="save"
      size={24}
      color={Colors.primary_500}
      onPress={handleSubmit(save)}
    />
  );

  const DeleteButton = () => (
    <Icon
      name="trash"
      size={24}
      color={Colors.primary_500}
      onPress={() => deleteForm()}
    />
  );

  const FormBuilderSelectionMenu = (props: any) => (
    <View style={styles.formSelectionButtonsContainer}>{props.children}</View>
  );

  const AddNewFormButton = () => (
    <Button
      style={styles.selectionMenuButton}
      text="Add"
      textColor={Colors.base_colors_white}
      lightColor={Colors.primary_500}
      darkColor={Colors.primary_500}
      onPress={() => {
        setCurrentState(State.Creating);
        append(emptyQuestion, { focusName: `questions.1.questionText` });
      }}
    ></Button>
  );

  const EditFormButton = () => (
    <Button
      style={styles.selectionMenuButton}
      text="Edit"
      textColor={Colors.base_colors_white}
      lightColor={Colors.primary_500}
      darkColor={Colors.primary_500}
      onPress={() => {
        setCurrentState(State.EditSelection);
      }}
    ></Button>
  );

  const DeleteFormButton = () => (
    <Button
      style={styles.selectionMenuButton}
      text="Delete"
      textColor={Colors.base_colors_white}
      lightColor={Colors.primary_500}
      darkColor={Colors.primary_500}
      onPress={() => {
        setCurrentState(State.DeleteSelection);
      }}
    ></Button>
  );

  const FormSelection = (props: any) => (
    <View style={styles.formSelectionContainer}>{props.children}</View>
  );

  const EditFormDropDown = () => (
    <Dropdown
      style={styles.formSelectionDropdown}
      data={forms}
      placeholder="Select Form"
      lightColor={Colors.tinted_grey_100}
      darkColor={Colors.nuetral_grey_700}
      name="editForm"
      control={control}
      errors={errors}
      required={false}
      label="Select a form to edit"
      setValue={setValue}
    ></Dropdown>
  );

  const DeleteFormDropDown = () => (
    <Dropdown
      data={forms}
      style={styles.formSelectionDropdown}
      placeholder="Select Form"
      lightColor={Colors.tinted_grey_100}
      darkColor={Colors.nuetral_grey_700}
      name="deleteForm"
      control={control}
      errors={errors}
      required={false}
      label="Select a form to Delete"
      setValue={setValue}
    ></Dropdown>
  );

  const FormContainer = (props: any) => (
    <View style={styles.formContainer}>{props.children}</View>
  );

  const FormName = () => (
    <Input
      placeholder="Form Name"
      label="Form Name"
      lightColor={Colors.tinted_grey_100}
      darkColor={Colors.nuetral_grey_700}
      control={control}
      errors={errors}
      name="formName"
      required={true}
      errorMessage="Form Name is required"
    />
  );

  const QuestionListHeaderContainer = (props: any) => (
    <View style={{ gap: 4 }}>{props.children}</View>
  );

  const QuestionListHeader = (props: any) => (
    <View style={styles.QuestionListHeader}>{props.children}</View>
  );

  const QuestionListHeaderText = () => (
    <Text size={16} weight="SemiBold" lightColor={Colors.nuetral_grey_700}>
      Questions
    </Text>
  );

  const AddQuestionButton = () => (
    <Icon
      name="plus"
      size={24}
      color={Colors.primary_500}
      onPress={() => append(emptyQuestion)}
    />
  );

  const QuestionListContainer = (props: any) => (
    <KeyboardAwareScrollView
      showsVerticalScrollIndicator={false}
      showsHorizontalScrollIndicator={false}
      keyboardShouldPersistTaps="always"
    >
      {props.children}
    </KeyboardAwareScrollView>
  );

  const QuestionList = (props: any) => (
    <View style={styles.questionList}>{props.children}</View>
  );

  const Questions = () => (
    <>
      {fields.map((question, index) => {
        return <FormQuestion index={index} question={question} />;
      })}
    </>
  );

  return (
    <FormProvider {...formMethods}>
      <FormBuilder>
        <PageHeader
          leftComponent={<BackButton />}
          centerComponent={<FormBuilderTitle />}
          rightComponent={
            <>
              {match(currentState)
                .with(State.Creating, () => <SaveButton />)
                .with(State.ReadyForEdit, () => <EditButton />)
                .with(State.ReadyForDelete, () => <DeleteButton />)
                .otherwise(() => (
                  <></>
                ))}
            </>
          }
        ></PageHeader>

        {currentState === State.Default && (
          <FormBuilderSelectionMenu>
            <AddNewFormButton />
            <EditFormButton />
            <DeleteFormButton />
          </FormBuilderSelectionMenu>
        )}

        <FormSelection>
          {(currentState === State.EditSelection ||
            currentState === State.ReadyForEdit) && <EditFormDropDown />}
          {(currentState === State.DeleteSelection ||
            currentState === State.ReadyForDelete) && <DeleteFormDropDown />}
          {(currentState === State.Creating ||
            currentState === State.ReadyForEdit ||
            currentState === State.ReadyForDelete) && (
            <FormContainer>
              <FormName />
              <QuestionListHeaderContainer>
                <QuestionListHeader>
                  <QuestionListHeaderText />
                  <AddQuestionButton />
                </QuestionListHeader>
                <Line orientation={Orientation.Horizontal} />
              </QuestionListHeaderContainer>
              <QuestionListContainer>
                <QuestionList>
                  <Questions />
                </QuestionList>
              </QuestionListContainer>
            </FormContainer>
          )}
        </FormSelection>
        <BottomModal
          text={bottomModalText}
          show={showBottomModal}
          onClosed={() => {
            setShowBottomModal(false);
          }}
        ></BottomModal>
      </FormBuilder>
    </FormProvider>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  selectionMenuButton: {
    width: 100,
  },
  formSelectionButtonsContainer: {
    flex: 0.75,
    alignItems: "center",
    justifyContent: "center",
    gap: 16,
  },
  formSelectionContainer: {
    zIndex: 0,
    elevation: 1,
    paddingHorizontal: 32,
    gap: 16,
  },
  formContainer: {
    gap: 16,
  },
  formSelectionDropdown: {
    zIndex: 10,
  },
  QuestionListHeader: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
  },
  questionContainer: {
    gap: 16,
    borderColor: Colors.nuetral_grey_400,
    borderWidth: 1,
    padding: 8,
    borderRadius: 8,
  },
  questionHeader: {
    flexDirection: "row",
    justifyContent: "space-between",
  },
  questionList: {
    gap: 16,
    paddingBottom: 700,
  },
  questionButtonContainer: {
    flexDirection: "row",
    gap: 8,
  },
  questionCheckboxContainer: {
    flexDirection: "row",
    gap: 16,
  },
});
